import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Check,
  ChevronRight,
  Clock,
} from 'lucide-react';
import { useSessao } from '../auth/SessaoContext';
import { BotaoLink } from '../componentes/ui';
import { GUIAS, type Guia } from '../conteudo/guias';
import { registrarVisita } from '../util/visita';
import { medirCliques } from '../util/clique';
import { Cabecalho } from './publico/Cabecalho';
import { ListaPassos, Passo } from './publico/Passo';
import { Rodape } from './publico/Rodape';
import { SituacaoCaptura } from './publico/Captura';
import { useCapturaPublica } from './publico/useCapturaPublica';
import base from './publico/publico.module.css';
import css from './Guia.module.css';

const SITE = 'https://baixaxml.com.br';

/** Âncora estável a partir do título ("O que é a chave" → "o-que-e-a-chave"). */
function ancora(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Minutos de leitura, a ~200 palavras por minuto. Determinístico (SSR). */
function minutosDeLeitura(guia: Guia): number {
  const texto = [
    guia.chamada,
    ...guia.secoes.flatMap((s) => [
      s.titulo,
      ...s.paragrafos,
      ...(s.lista ?? []),
    ]),
    ...guia.passos.flatMap((p) => [p.titulo, p.texto]),
    ...guia.perguntas.flatMap((p) => [p.pergunta, p.resposta]),
  ].join(' ');
  return Math.max(1, Math.round(texto.split(/\s+/).length / 200));
}

/**
 * Página de conteúdo para a busca orgânica (ver `conteudo/guias.ts`).
 *
 * Texto corrido, títulos de verdade (h1/h2/h3) e as respostas do FAQ ABERTAS:
 * é o mesmo conteúdo que o `prerenderizar.mjs` marca como FAQPage, e marcação
 * estruturada só vale se a resposta estiver visível na página.
 */
export function PaginaGuia({ guia }: { guia: Guia }) {
  const { usuario } = useSessao();

  // Mesmo motivo da Privacidade: navegando pela SPA o documento ainda é o da
  // home, e título e `canonical` continuariam apontando para `/`.
  useEffect(() => {
    const tag = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    const anterior = tag?.href;
    const tituloAnterior = document.title;
    if (tag) tag.href = `${SITE}${guia.rota}`;
    document.title = guia.titulo;
    return () => {
      if (tag && anterior) tag.href = anterior;
      document.title = tituloAnterior;
    };
  }, [guia]);

  // Conta como visita da página pública — é por estas páginas que a busca
  // orgânica deve começar a chegar, e o funil precisa enxergar isso.
  useEffect(() => {
    registrarVisita();
    const aoDecidir = () => registrarVisita();
    window.addEventListener('bx:consentimento', aoDecidir);
    return () => window.removeEventListener('bx:consentimento', aoDecidir);
  }, []);

  useEffect(() => medirCliques(), []);

  const captura = useCapturaPublica(!!guia.captura);

  const outros = GUIAS.filter((g) => g.rota !== guia.rota);
  const [destino, rotuloCta] = !usuario
    ? ['/criar-conta', 'Criar conta grátis']
    : !guia.captura
      ? ['/painel/nova-busca', 'Nova busca']
      : captura?.disponivel
        ? ['/painel/empresas', 'Cadastrar empresa']
        : ['/painel/suporte', 'Pedir acesso pelo chat'];

  const indice = [
    ...guia.secoes.map((s) => ({ id: ancora(s.titulo), titulo: s.titulo })),
    { id: 'passo-a-passo', titulo: 'Passo a passo' },
    { id: 'perguntas-frequentes', titulo: 'Perguntas frequentes' },
  ];

  return (
    <div className={base.pagina}>
      <Cabecalho />

      <header className={css.abertura}>
        <div className={`${base.conteudo} ${css.aberturaConteudo}`}>
          {/* Migalhas: o mesmo caminho que o BreadcrumbList do prerender. */}
          <nav aria-label="Você está em" className={css.migalhas}>
            <Link to="/">Início</Link>
            <ChevronRight aria-hidden="true" />
            <span aria-current="page">{guia.rotulo}</span>
          </nav>
          <h1 className={css.titulo}>{guia.h1}</h1>
          <p className={css.chamada}>{guia.chamada}</p>
          {guia.captura && <SituacaoCaptura dados={captura} />}
          <div className={css.aberturaRodape}>
            <BotaoLink
              to={destino}
              data-clique="criar-conta-topo-guia"
              aparencia="destaque"
              grande
              iconeDireita={<ArrowRight />}
            >
              {rotuloCta}
            </BotaoLink>
            <span className={css.meta}>
              <BookOpen aria-hidden="true" /> Guia
              <span aria-hidden="true">·</span>
              <Clock aria-hidden="true" /> {minutosDeLeitura(guia)} min de
              leitura
            </span>
          </div>
        </div>
      </header>

      <div className={`${base.conteudo} ${css.grade}`}>
        <main className={css.artigo}>
          {guia.secoes.map((s) => (
            <section key={s.titulo} id={ancora(s.titulo)} className={css.secao}>
              <h2>{s.titulo}</h2>
              {s.paragrafos.map((p) => (
                <p key={p}>{p}</p>
              ))}
              {s.lista && (
                <ul className={css.lista}>
                  {s.lista.map((item) => (
                    <li key={item}>
                      <Check aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <section id="passo-a-passo" className={css.secao}>
            <h2>Passo a passo</h2>
            <div className={css.caixaPassos}>
              <ListaPassos vertical>
                {guia.passos.map((p, i) => (
                  <Passo
                    key={p.titulo}
                    numero={String(i + 1)}
                    titulo={p.titulo}
                    texto={p.texto}
                  />
                ))}
              </ListaPassos>
            </div>
          </section>

          <section id="perguntas-frequentes" className={css.secao}>
            <h2>Perguntas frequentes</h2>
            <div className={css.perguntas}>
              {guia.perguntas.map((p) => (
                <div key={p.pergunta} className={css.pergunta}>
                  <h3>{p.pergunta}</h3>
                  <p>{p.resposta}</p>
                </div>
              ))}
            </div>
          </section>

          <section className={css.cta}>
            {guia.captura ? (
              <>
                <h2>Pare de correr atrás de XML de entrada</h2>
                <p>
                  Crie a conta e cadastre o certificado A1 das empresas. Para
                  notas com mais de 90 dias, o download pela chave ou pelo SPED
                  continua à mão, pago por XML entregue —{' '}
                  <a href="/#captura" data-clique="ver-captura-guia">
                    veja como funciona a captura
                  </a>
                  .
                </p>
              </>
            ) : (
              <>
                <h2>Teste com as suas notas</h2>
                <p>
                  Sem mensalidade e sem certificado digital. Crie a conta, traga
                  as chaves ou o SPED e veja quanto custa antes de começar.
                  Preços na{' '}
                  <a href="/#precos" data-clique="ver-precos-guia">
                    tabela por volume
                  </a>
                  .
                </p>
              </>
            )}
            <div>
              <BotaoLink
                to={destino}
                data-clique="criar-conta-fim-guia"
                aparencia="destaque"
                iconeDireita={<ArrowRight />}
              >
                {rotuloCta}
              </BotaoLink>
            </div>
          </section>

          <nav aria-label="Outros guias" className={css.outros}>
            <h2>Veja também</h2>
            <ul>
              {outros.map((g) => (
                <li key={g.rota}>
                  <Link to={g.rota} data-clique="guia-relacionado">
                    <span>{g.h1}</span>
                    <ArrowRight aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </main>

        <aside className={css.lateral}>
          <nav aria-label="Neste guia" className={css.indice}>
            <span className={css.indiceTitulo}>Neste guia</span>
            {indice.map((i) => (
              <a key={i.id} href={`#${i.id}`}>
                {i.titulo}
              </a>
            ))}
          </nav>

          <div className={css.cartaoLateral}>
            {guia.captura ? (
              <>
                <strong>Notas de entrada sem pedir ao cliente</strong>
                <ul>
                  <li>
                    <Check aria-hidden="true" /> Direto da SEFAZ, todo dia
                  </li>
                  <li>
                    <Check aria-hidden="true" /> Ciência da Operação automática
                  </li>
                  <li>
                    <Check aria-hidden="true" /> Vários CNPJs numa conta
                  </li>
                </ul>
              </>
            ) : (
              <>
                <strong>XML de NF-e sem certificado digital</strong>
                <ul>
                  <li>
                    <Check aria-hidden="true" /> Pela chave ou direto do SPED
                  </li>
                  <li>
                    <Check aria-hidden="true" /> Paga só o XML entregue
                  </li>
                  <li>
                    <Check aria-hidden="true" /> Recarga por Pix, sem
                    mensalidade
                  </li>
                </ul>
              </>
            )}
            <BotaoLink
              to={destino}
              data-clique="criar-conta-lateral-guia"
              larguraTotal
            >
              {rotuloCta}
            </BotaoLink>
            <span className={css.notaLateral}>
              <BadgeCheck aria-hidden="true" /> XML original, com a assinatura
              digital do emitente
            </span>
          </div>
        </aside>
      </div>

      <Rodape />
    </div>
  );
}
